/** Plain-language definitions of each OSHA hazard class, keyed by root class code (29 CFR 1910.1200 App. A & B). */
export const HAZARD_CLASS_DESCRIPTIONS: Readonly<Record<string, string>> = {
  // Health
  ACUTE_TOXICITY_ORAL:
    "Serious harm or death after a single swallowed dose, or several doses within 24 hours.",
  ACUTE_TOXICITY_DERMAL:
    "Serious harm or death after a single skin exposure, or several exposures within 24 hours.",
  ACUTE_TOXICITY_INHALATION_GASES: "Serious harm or death after a 4-hour inhalation of the gas.",
  ACUTE_TOXICITY_INHALATION_VAPORS: "Serious harm or death after a 4-hour inhalation of the vapor.",
  ACUTE_TOXICITY_INHALATION_DUSTS_AND_MISTS:
    "Serious harm or death after a 4-hour inhalation of the dust or mist.",
  ASPIRATION_HAZARD:
    "Can cause severe lung injury or death if the liquid enters the airway, e.g. when swallowed or vomited.",
  CARCINOGENICITY: "Can cause cancer or increase its incidence.",
  GERM_CELL_MUTAGENICITY: "Can cause heritable genetic mutations in human germ cells.",
  REPRODUCTIVE_TOXICITY:
    "Can impair sexual function or fertility, or harm the development of offspring.",
  RESPIRATORY_SENSITIZER: "Can cause allergy, asthma, or breathing difficulty when inhaled.",
  SKIN_SENSITIZER: "Can cause an allergic skin reaction after skin contact.",
  SERIOUS_EYE_DAMAGE:
    "Causes eye tissue damage or vision loss that is not fully reversible within 21 days.",
  EYE_IRRITATION: "Causes eye changes that fully reverse within 21 days.",
  SIMPLE_ASPHYXIANT: "A gas that displaces oxygen in the air, which can cause rapid suffocation.",
  SKIN_CORROSION: "Causes irreversible skin damage, such as visible necrosis through the skin.",
  SKIN_IRRITATION: "Causes reversible skin damage.",
  SPECIFIC_TARGET_ORGAN_TOXICITY_SINGLE_EXPOSURE:
    "Damages specific organs, or causes narcosis or respiratory irritation, after a single exposure.",
  SPECIFIC_TARGET_ORGAN_TOXICITY_REPEATED_OR_PROLONGED_EXPOSURE:
    "Damages specific organs after repeated or prolonged exposure.",
  HAZARD_NOT_OTHERWISE_CLASSIFIED_HEALTH:
    "A health hazard identified for the chemical that does not fit any other class.",
  // Physical
  AEROSOLS:
    "A non-refillable container holding gas under pressure, released as a spray, foam, or paste.",
  CHEMICALS_UNDER_PRESSURE:
    "A liquid or solid pressurized with gas in a container other than an aerosol dispenser.",
  FLAMMABLE_GASES: "A gas with a flammable range in air at 20°C and standard pressure.",
  FLAMMABLE_LIQUIDS: "A liquid with a flash point at or below 93°C (199.4°F).",
  FLAMMABLE_SOLIDS: "A solid that ignites readily or can cause or contribute to fire by friction.",
  COMBUSTIBLE_DUST: "Fine particles that can form an explosive mixture when suspended in air.",
  CORROSIVE_TO_METAL: "Can materially damage or destroy metals.",
  DESENSITIZED_EXPLOSIVES:
    "An explosive wetted or diluted so it doesn't mass explode or burn too quickly.",
  EXPLOSIVES:
    "Can rapidly release gas, heat, and pressure by chemical reaction; the division reflects the hazard type.",
  GAS_UNDER_PRESSURE:
    "A gas held at 200 kPa or more, or as a refrigerated liquid, which can explode if heated or cause cold burns.",
  IN_CONTACT_WITH_WATER_EMITS_FLAMMABLE_GASES:
    "Reacts with water to release flammable gases in dangerous amounts.",
  ORGANIC_PEROXIDES:
    "An unstable organic compound that can decompose violently; Type A is the most hazardous.",
  OXIDIZING_GASES: "A gas that can cause or contribute to combustion more than air does.",
  OXIDIZING_LIQUIDS: "A liquid that can cause or contribute to combustion by releasing oxygen.",
  OXIDIZING_SOLIDS: "A solid that can cause or contribute to combustion by releasing oxygen.",
  PYROPHORIC_LIQUIDS: "A liquid that can ignite within 5 minutes of contact with air.",
  PYROPHORIC_SOLIDS: "A solid that can ignite within 5 minutes of contact with air.",
  SELF_HEATING_CHEMICALS:
    "Can heat itself on contact with air, without an energy source, and may ignite in large amounts.",
  SELF_REACTIVE_CHEMICALS:
    "A thermally unstable chemical that can decompose violently without oxygen; Type A is the most hazardous.",
  HAZARD_NOT_OTHERWISE_CLASSIFIED_PHYSICAL:
    "A physical hazard identified for the chemical that does not fit any other class.",
};
